import React, { useState } from 'react';
import { createSearchParams, useMatch, useNavigate, useOutletContext } from 'react-router-dom';
import { TDR, toLuxon } from 'tdr-common';
import { useCheckoutSession } from '../api/getCheckoutSession';
import { useReleaseCheckoutSession } from '../api/releaseCheckoutSession';
import { AbandonSession } from '../components/__design-system/modal/checkout/abandon-session';
import { AreYouStillThere } from '../components/__design-system/modal/checkout/are-you-still-there';
import { SquareIconButton } from '../components/buttons/SquareIconButton';
import { CheckoutTimer } from '../components/checkout/CheckoutTimer';
import { Header } from '../components/checkout/Header';
import { baseCheckoutNavConfig } from '../components/checkout/steps/config';
import { ProgressBar } from '../components/progress-bar/ProgressBar';
import { useCheckoutContext } from '../context/new/CheckoutContext';
import { analytics } from '../helpers/analytics';
import { resolveLocalizedText } from '../helpers/resolveLocalizedText';
import { useTable } from '@/api/getTable';
import { RestaurantGuardOutletContext } from '@/routes/RestaurantGuard';
import { useTranslation } from 'react-i18next';

type CheckoutLayoutProps = {
	session: TDR.CheckoutSession.CheckoutSession;
	children: React.ReactNode;
};

export const CheckoutLayout = ({ session, children }: CheckoutLayoutProps) => {
	const { t, i18n } = useTranslation();
	const { restaurant, featureFlags } = useOutletContext<RestaurantGuardOutletContext>();
	const [isConfirmExitOpen, setIsConfirmExitOpen] = useState(false);
	const [backButtonClicked, setBackButtonClicked] = useState(false);

	const {
		data: { checkoutInvoice }
	} = useCheckoutSession(session.id);
	const { checkoutState, hasContactDetailsInput, navConfig: dynamicNavConfig } = useCheckoutContext();

	const { mutate: releaseCheckoutSession } = useReleaseCheckoutSession();

	const navigate = useNavigate();

	// Use dynamic navConfig from context if available, otherwise fallback to static config
	const navConfig = dynamicNavConfig || baseCheckoutNavConfig	;

	const luxonStartDateTime = toLuxon(session.time, restaurant.timezone);

	const requiresCreditCard = restaurant.allowNoShowFees || checkoutInvoice?.total > 0;

	const confirmationRouteMatch = useMatch(`${restaurant.slug}/checkout/${session.id}/confirmation`);
	const match = useMatch(`${restaurant.slug}/checkout/${session.id}/:activeStep`);

	const activeStep = match?.params?.activeStep;
	const totalSteps = navConfig.confirmation.order - (requiresCreditCard ? 0 : 1); // 1 less step when credit card not required
	const progressValue = (navConfig[activeStep]?.order / totalSteps) * 100;

	const { data: table } = useTable(session?.tableId);

	const handleSessionEnd = () => {
		navigate(`/${restaurant.slug}/explore`, { replace: true });
	};

	const handleSessionContinue = () => {
		// refresh page -> will trigger getCheckoutSession and restart the timer if it has expired
		navigate(0);
	};

	const handleExitClick = () => {
		if (hasContactDetailsInput) {
			setIsConfirmExitOpen(true);
		}
		else {
			handleConfirmExit();
		}
	};

	const handleCancelExit = () => {
		setIsConfirmExitOpen(false);
		setBackButtonClicked(false);
	};

	const handleConfirmExit = () => {
		analytics.track('Checkout Session Exited');
		releaseCheckoutSession({ checkoutId: session.id });
		// Need to update this. Talk about it on monday
		navigate({
			pathname: `/${restaurant.slug}/explore`,
			search: createSearchParams({
				groupSize: session.guests.toString(),
				date: luxonStartDateTime.toFormat('yyyy-LL-dd'),
				time: luxonStartDateTime.toFormat('HH:mm')
			}).toString()
		});
	};

	const navigateBack = () => {
		analytics.track('Checkout Session Exited');
		releaseCheckoutSession({ checkoutId: session.id });
		navigate({
			pathname: `/${restaurant.slug}/explore/${table.slug}`,
			search: createSearchParams({
				groupSize: session.guests.toString(),
				date: luxonStartDateTime.toFormat('yyyy-LL-dd'),
				time: luxonStartDateTime.toFormat('HH:mm')
			}).toString()
		});
	};

	const handleBackButtonClick = () => {
		// Check if this is the first step in the checkout flow
		const isFirstStep = navConfig[activeStep]?.prev === null || navConfig[activeStep]?.prev === undefined;

		if (isFirstStep) {
			setBackButtonClicked(true);
			if (hasContactDetailsInput) {
				setIsConfirmExitOpen(true);
			}
			else {
				navigateBack();
			}
		}
		else {
			navigate(navConfig[activeStep]?.prev);
		}
	};

	return (
		<>
			<div className="bg-tablz-dark-gray-1 h-full sm:w-[400px] flex flex-col">
				<div className="fixed top-0 left-0 right-0 z-20 sm:sticky">
					<Header
						title={navConfig[activeStep]?.titleKey ? t(`checkout.step_titles.${navConfig[activeStep].titleKey}`) : resolveLocalizedText(navConfig[activeStep]?.title || '', i18n.language)}
						subtitle={t('titles.checkout')}
						iconLeft={<SquareIconButton variant='back' />}
						iconLeftOnClick={handleBackButtonClick}
						iconRight={<SquareIconButton variant='exit' />}
						iconRightOnClick={handleExitClick}
					/>

					{featureFlags?.privateEvents ? null : <CheckoutTimer timeRemaining={checkoutState?.timer?.secondsLeft} />}

					<ProgressBar value={progressValue} />
				</div>

				{/* children here is the <Outlet /> passed from the <CheckoutGuard /> */}
				{/* includes the checkout page body and footer */}
				{children}
			</div>

			<AbandonSession
				open={isConfirmExitOpen}
				onOpenChange={setIsConfirmExitOpen}
				onAbandon={backButtonClicked ? navigateBack : handleConfirmExit}
				onDismiss={handleCancelExit}
			/>

			<AreYouStillThere
				open={!isConfirmExitOpen && !confirmationRouteMatch && checkoutState.timer?.secondsLeft === 0}
				onContinue={handleSessionContinue}
				onEnd={handleSessionEnd}
			/>
		</>
	);
};
